import { Fragment, useState } from "react";
import { Link } from "react-router-dom";
import { budgetApi } from "../lib/api";
import { useCompany } from "../masters/reference";
import { useLoad } from "../ui/useLoad";
import { ErrorNote, Loading, PageHead } from "../ui/bits";
import { fmtAmount } from "../lib/money";
import { fmtDate } from "../companies/CompanyPicker";
import { iso } from "./period";
import { toUrl } from "../shell/routes";

// cash in hand and at the bank: a plain amount, below zero when overdrawn (not Dr / Cr)
const cash = (n: number) => (n < 0 ? `−${fmtAmount(-n)}` : fmtAmount(n));
const SRC: Record<string, string> = { bill: "Open bill", order: "Order not yet invoiced", recurring: "Recurring" };

// FS13: the 13-week cash forecast (sql/241): cash and bank today, then week by week what customers' open bills,
// vendors' open bills, open orders and recurring invoices and bills bring in and take out.
export function CashForecast() {
  const { company } = useCompany();
  const [asOn, setAsOn] = useState(iso(new Date()));
  const [weeks, setWeeks] = useState(13);
  const [open, setOpen] = useState<number | null>(null);
  const f = useLoad(() => budgetApi.forecast(company.id, asOn, weeks), [company.id, asOn, weeks]);
  const d = f.data;
  const low = d ? Math.min(d.cash_today, ...d.weeks.map((w) => w.closing)) : 0;   // today counts: an overdraft now is the low point
  const short = d?.weeks.find((w) => w.closing < 0);
  const max = d ? Math.max(1, ...d.weeks.map((w) => Math.max(w.money_in, w.money_out))) : 1;
  return (
    <section className="page">
      <PageHead crumb="Reports" title="Cash forecast" />
      <div className="toolbar params">
        <label>From<input type="date" value={asOn} onChange={(e) => setAsOn(e.target.value)} /></label>
        <label>Weeks<select value={weeks} onChange={(e) => setWeeks(Number(e.target.value))}>{[4, 8, 13, 26].map((n) => <option key={n} value={n}>{n}</option>)}</select></label>
        <Link to={toUrl(`reports/r/cash_forecast?as_on=${asOn}`)}>As a report (Excel, PDF, email)</Link>
      </div>
      {!d ? (f.error ? <ErrorNote text={f.error.message} /> : <Loading />) : <>
        <div className="stats">
          <div className="stat"><span className="muted small">Cash and bank now</span><b className={d.cash_today < 0 ? "warn" : ""}>{cash(d.cash_today)}</b></div>
          <div className="stat"><span className="muted small">Coming in</span><b>{fmtAmount(d.weeks.reduce((s, w) => s + w.money_in, 0))}</b></div>
          <div className="stat"><span className="muted small">Going out</span><b>{fmtAmount(d.weeks.reduce((s, w) => s + w.money_out, 0))}</b></div>
          <div className={`stat ${low < 0 ? "warn" : ""}`}><span className="muted small">Lowest point</span><b>{cash(low)}</b></div>
        </div>
        {d.cash_today < 0 && <p className="warn small">Cash and bank are overdrawn by {fmtAmount(-d.cash_today)} today in the books: a payment entered before its money came in, or the opening bank balance not entered yet (Accounting › Opening Balances).</p>}
        {short && <p className="warn small">Cash runs short in week {short.week_no} ({fmtDate(short.week_from)}). Chase receipts or move payments.</p>}
        <table className="grid-table">
          <thead><tr><th>Week</th><th>From</th><th className="num">Opening</th><th className="num">In</th><th className="num">Out</th><th className="num">Closing</th><th style={{ width: "28%" }}></th></tr></thead>
          <tbody>{d.weeks.map((w) => <Fragment key={w.week_no}>
            <tr className={open === w.week_no ? "selected" : ""}>
              <td><button className="link" onClick={() => setOpen(open === w.week_no ? null : w.week_no)}>{w.week_no}</button></td>
              <td>{fmtDate(w.week_from)}</td><td className="num">{cash(w.opening)}</td>
              <td className="num">{w.money_in ? fmtAmount(w.money_in) : ""}</td><td className="num">{w.money_out ? fmtAmount(w.money_out) : ""}</td>
              <td className={`num ${w.closing < 0 ? "warn" : ""}`}><b>{cash(w.closing)}</b></td>
              <td><div className="bar-pair" title={`In ${fmtAmount(w.money_in)} · out ${fmtAmount(w.money_out)}`}>
                <span className="bar in" style={{ width: `${(w.money_in / max) * 100}%` }} /><span className="bar out" style={{ width: `${(w.money_out / max) * 100}%` }} /></div></td>
            </tr>
            {open === w.week_no && <tr><td></td><td colSpan={6}>
              {d.items.filter((i) => i.week_no === w.week_no).length === 0 ? <span className="muted small">Nothing expected this week.</span> :
                <table className="grid-table compact"><tbody>{d.items.filter((i) => i.week_no === w.week_no).map((i, k) => <tr key={k}>
                  <td>{fmtDate(i.due)}</td><td>{i.direction === "in" ? "In" : "Out"}</td><td>{i.label}</td><td className="muted">{SRC[i.source]}{i.ref ? ` ${i.ref}` : ""}</td>
                  <td className="num">{fmtAmount(i.amount)}</td></tr>)}</tbody></table>}
            </td></tr>}
          </Fragment>)}</tbody>
        </table>
        <p className="muted small">From the books: customers' and vendors' open bills by due date (overdue ones in week 1), sales and purchase orders not yet
          invoiced (by their expected date, plus the party's credit days, with GST), and active recurring invoices and bills. Salaries, taxes and loan
          payments come in when they are entered as recurring bills or open bills.</p>
      </>}
    </section>
  );
}
